/**
 * FP-Header — Flower Point Premium Navigation
 * Flower Point | Palm Gardens LLC | Dubai, UAE
 */

(function () {
  'use strict';

  /* ── Constants ──────────────────────────────────────────── */
  const STORAGE_KEY_ANNOUNCE = 'fp_announcement_closed';
  const SCROLL_THRESHOLD = 10; // px before sticky kicks in

  /* ── State ──────────────────────────────────────────────── */
  let isScrolled = false;
  let scrollRAF = null;
  let resizeRAF = null;
  let isMobileNavOpen = false;
  let isMobileSearchOpen = false;

  /* ── Elements ───────────────────────────────────────────── */
  const wrapper     = document.getElementById('fp-header-wrapper');
  const header      = document.getElementById('fp-header');
  const announcement = document.getElementById('fp-announcement');
  const announceCls = document.getElementById('fp-announcement-close');
  const mobileMenuBtn = document.getElementById('fp-mobile-menu-btn');
  const mobileNav   = document.getElementById('fp-mobile-nav');
  const mobileNavClose = document.getElementById('fp-mobile-nav-close');
  const mobileOverlay = document.getElementById('fp-mobile-overlay');
  const mobileSearchBtn = document.getElementById('fp-search-mobile-btn');
  const mobileSearch = document.getElementById('fp-mobile-search');
  const mobileSearchClose = document.getElementById('fp-mobile-search-close');
  const mobileSearchInput = document.getElementById('fp-mobile-search-input');
  const cartCount   = document.getElementById('fp-cart-count');
  const spacer      = document.getElementById('fp-header-spacer');
  const mobileToggles = document.querySelectorAll('.fp-mobile-nav__toggle');
  const dropdownItems = document.querySelectorAll('.fp-nav__item--has-dropdown');

  /* ── Utilities ──────────────────────────────────────────── */
  function setSpacerHeight() {
    if (!spacer || !header) return;
    // On mobile, header is always visible (no primary-collapse)
    const height = header.offsetHeight;
    const announcementH = (announcement && !announcement.classList.contains('is-hidden'))
      ? announcement.offsetHeight
      : 0;
    spacer.style.height = (height + announcementH) + 'px';
  }

  function getHeaderTop() {
    if (!announcement || announcement.classList.contains('is-hidden')) return 0;
    return announcement.offsetHeight;
  }

  function positionHeader() {
    if (!header) return;
    const top = getHeaderTop();
    header.style.top = top + 'px';
  }

  /* ── Scroll Handling ────────────────────────────────────── */
  function onScroll() {
    if (scrollRAF) cancelAnimationFrame(scrollRAF);
    scrollRAF = requestAnimationFrame(handleScroll);
  }

  function handleScroll() {
    const scrollY = window.scrollY;
    const shouldScroll = scrollY > SCROLL_THRESHOLD;

    if (shouldScroll !== isScrolled) {
      isScrolled = shouldScroll;
      header && header.classList.toggle('is-scrolled', isScrolled);

      // On desktop: move header to top of viewport when scrolled (announcement hidden above)
      if (window.innerWidth > 768) {
        if (isScrolled) {
          header.style.top = '0';
        } else {
          positionHeader();
        }
      }

      setSpacerHeight();
    }
  }

  /* ── Announcement Bar ───────────────────────────────────── */
  function initAnnouncement() {
    if (!announcement) return;

    // Check if user previously closed it this session
    if (sessionStorage.getItem(STORAGE_KEY_ANNOUNCE) === '1') {
      hideAnnouncement(false);
      return;
    }

    positionHeader();
    setSpacerHeight();

    if (announceCls) {
      announceCls.addEventListener('click', () => {
        hideAnnouncement(true);
      });
    }
  }

  function hideAnnouncement(save) {
    if (!announcement) return;
    announcement.classList.add('is-hidden');
    if (save) sessionStorage.setItem(STORAGE_KEY_ANNOUNCE, '1');

    // Re-position header
    setTimeout(() => {
      positionHeader();
      setSpacerHeight();
    }, 50);
  }

  /* ── Mobile Nav ─────────────────────────────────────────── */
  function openMobileNav() {
    if (!mobileNav || !mobileOverlay) return;
    isMobileNavOpen = true;
    mobileNav.classList.add('is-open');
    mobileOverlay.classList.add('is-visible');
    mobileNav.setAttribute('aria-hidden', 'false');
    mobileOverlay.setAttribute('aria-hidden', 'false');
    mobileMenuBtn && mobileMenuBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    // Focus first focusable element
    setTimeout(() => {
      const firstLink = mobileNav.querySelector('a, button');
      if (firstLink) firstLink.focus();
    }, 400);
  }

  function closeMobileNav() {
    if (!mobileNav || !mobileOverlay) return;
    isMobileNavOpen = false;
    mobileNav.classList.remove('is-open');
    mobileOverlay.classList.remove('is-visible');
    mobileNav.setAttribute('aria-hidden', 'true');
    mobileOverlay.setAttribute('aria-hidden', 'true');
    mobileMenuBtn && mobileMenuBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    mobileMenuBtn && mobileMenuBtn.focus();
  }

  function initMobileNav() {
    if (mobileMenuBtn) {
      mobileMenuBtn.addEventListener('click', () => {
        isMobileNavOpen ? closeMobileNav() : openMobileNav();
      });
    }

    if (mobileNavClose) {
      mobileNavClose.addEventListener('click', closeMobileNav);
    }

    if (mobileOverlay) {
      mobileOverlay.addEventListener('click', () => {
        if (isMobileNavOpen) closeMobileNav();
        if (isMobileSearchOpen) closeMobileSearch();
      });
    }

    // Mobile sub-menu toggles
    mobileToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        const targetId = toggle.getAttribute('aria-controls');
        const sublist = document.getElementById(targetId);

        // Close all others
        mobileToggles.forEach((t) => {
          if (t !== toggle) {
            t.setAttribute('aria-expanded', 'false');
            const sid = t.getAttribute('aria-controls');
            const sl = document.getElementById(sid);
            if (sl) {
              sl.classList.remove('is-open');
              sl.setAttribute('aria-hidden', 'true');
            }
          }
        });

        toggle.setAttribute('aria-expanded', String(!isExpanded));
        if (sublist) {
          sublist.classList.toggle('is-open', !isExpanded);
          sublist.setAttribute('aria-hidden', String(isExpanded));
          // Enable tab focus for sublinks
          const sublinks = sublist.querySelectorAll('a');
          sublinks.forEach((a) => {
            a.setAttribute('tabindex', isExpanded ? '-1' : '0');
          });
        }
      });
    });

    // Keyboard: close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (isMobileNavOpen) closeMobileNav();
        if (isMobileSearchOpen) closeMobileSearch();
        closeAllDropdowns();
      }
    });
  }

  /* ── Mobile Search ──────────────────────────────────────── */
  function openMobileSearch() {
    if (!mobileSearch) return;
    isMobileSearchOpen = true;
    mobileSearch.classList.add('is-open');
    mobileSearch.setAttribute('aria-hidden', 'false');
    setTimeout(() => {
      if (mobileSearchInput) mobileSearchInput.focus();
    }, 300);
  }

  function closeMobileSearch() {
    if (!mobileSearch) return;
    isMobileSearchOpen = false;
    mobileSearch.classList.remove('is-open');
    mobileSearch.setAttribute('aria-hidden', 'true');
  }

  function initMobileSearch() {
    if (mobileSearchBtn) {
      mobileSearchBtn.addEventListener('click', openMobileSearch);
    }
    if (mobileSearchClose) {
      mobileSearchClose.addEventListener('click', closeMobileSearch);
    }
  }

  /* ── Desktop Dropdowns ──────────────────────────────────── */
  function closeAllDropdowns() {
    dropdownItems.forEach((item) => {
      item.classList.remove('is-open');
      const link = item.querySelector('.fp-nav__link');
      if (link) link.setAttribute('aria-expanded', 'false');
      const dropdown = item.querySelector('.fp-dropdown');
      if (dropdown) {
        const links = dropdown.querySelectorAll('a, button');
        links.forEach((l) => l.setAttribute('tabindex', '-1'));
      }
    });
  }

  function initDropdowns() {
    dropdownItems.forEach((item) => {
      const link = item.querySelector('.fp-nav__link');
      const dropdown = item.querySelector('.fp-dropdown');

      if (!link || !dropdown) return;

      let closeTimeout;

      function openDropdown() {
        clearTimeout(closeTimeout);
        // Close others first
        dropdownItems.forEach((other) => {
          if (other !== item) {
            other.classList.remove('is-open');
            const ol = other.querySelector('.fp-nav__link');
            if (ol) ol.setAttribute('aria-expanded', 'false');
          }
        });
        item.classList.add('is-open');
        link.setAttribute('aria-expanded', 'true');
        // Make links focusable
        const dLinks = dropdown.querySelectorAll('a, button');
        dLinks.forEach((l) => l.setAttribute('tabindex', '0'));
      }

      function scheduleClose() {
        closeTimeout = setTimeout(() => {
          item.classList.remove('is-open');
          link.setAttribute('aria-expanded', 'false');
          const dLinks = dropdown.querySelectorAll('a, button');
          dLinks.forEach((l) => l.setAttribute('tabindex', '-1'));
        }, 120);
      }

      item.addEventListener('mouseenter', openDropdown);
      item.addEventListener('mouseleave', scheduleClose);
      dropdown.addEventListener('mouseenter', () => clearTimeout(closeTimeout));
      dropdown.addEventListener('mouseleave', scheduleClose);

      // Keyboard support
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isOpen = item.classList.contains('is-open');
          if (isOpen) {
            item.classList.remove('is-open');
            link.setAttribute('aria-expanded', 'false');
          } else {
            openDropdown();
            // Focus first dropdown link
            const firstLink = dropdown.querySelector('a');
            if (firstLink) {
              setTimeout(() => firstLink.focus(), 50);
            }
          }
        }
        if (e.key === 'Escape') {
          item.classList.remove('is-open');
          link.setAttribute('aria-expanded', 'false');
          link.focus();
        }
      });
    });
  }

  /* ── Cart Count (AJAX / Shopify Sections API) ───────────── */
  function updateCartCount(count) {
    if (!cartCount) return;
    cartCount.textContent = count;
    cartCount.setAttribute('data-count', count);

    // Pulse animation
    cartCount.classList.remove('fp-cart-pulse');
    void cartCount.offsetWidth; // force reflow
    cartCount.classList.add('fp-cart-pulse');
  }

  function initCartListener() {
    // Listen for Shopify cart update events
    document.addEventListener('cart:updated', (e) => {
      if (e.detail && typeof e.detail.itemCount !== 'undefined') {
        updateCartCount(e.detail.itemCount);
      }
    });

    // Also listen for standard Shopify Ajax Cart events
    document.addEventListener('cart:refresh', fetchCartCount);
  }

  function fetchCartCount() {
    fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        if (typeof data.item_count !== 'undefined') {
          updateCartCount(data.item_count);
        }
      })
      .catch(() => {});
  }

  /* ── Resize Handler ─────────────────────────────────────── */
  function onResize() {
    if (resizeRAF) cancelAnimationFrame(resizeRAF);
    resizeRAF = requestAnimationFrame(() => {
      setSpacerHeight();
      positionHeader();

      // Close mobile nav if resized to desktop
      if (window.innerWidth > 768 && isMobileNavOpen) {
        closeMobileNav();
      }
    });
  }

  /* ── Transparent Header ─────────────────────────────────── */
  function initTransparent() {
    if (!wrapper) return;
    const isTransparent = wrapper.dataset.transparent === 'true';
    const isHomepage = wrapper.dataset.homepage === 'true';

    if (isTransparent && isHomepage) {
      wrapper.classList.add('fp-header-wrapper--transparent');
    }
  }

  /* ── CSS Cart Badge Pulse (injected) ────────────────────── */
  function injectCartPulse() {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fp-cart-badge-pulse {
        0%   { transform: scale(1); }
        40%  { transform: scale(1.35); }
        70%  { transform: scale(0.92); }
        100% { transform: scale(1); }
      }
      .fp-cart-pulse {
        animation: fp-cart-badge-pulse 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      }
    `;
    document.head.appendChild(style);
  }

  /* ── Init ───────────────────────────────────────────────── */
  function init() {
    if (!wrapper || !header) return;

    injectCartPulse();
    initTransparent();
    initAnnouncement();
    initDropdowns();
    initMobileNav();
    initMobileSearch();
    initCartListener();

    // Initial spacer + position
    setSpacerHeight();
    positionHeader();

    // Event listeners
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    // Run scroll check immediately
    handleScroll();
  }

  /* ── DOM Ready ──────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();